// Enhanced logging utility for better debugging and user experience
class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.logHistory = [];
    this.maxHistorySize = 100;
  }

  // Create structured log entry
  createLogEntry(level, message, context = {}, error = null) {
    const entry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : null,
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // Keep log history for debugging
    this.logHistory.push(entry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }

    return entry;
  }

  // Enhanced console logging with context
  log(level, message, context = {}, error = null) {
    const entry = this.createLogEntry(level, message, context, error);
    
    if (this.isDevelopment) {
      const style = this.getLogStyle(level);
      console.groupCollapsed(`%c${level.toUpperCase()} ${entry.timestamp}`, style);
      console.log(`%c${message}`, 'font-weight: bold;');
      if (Object.keys(context).length > 0) {
        console.log('Context:', context);
      }
      if (error) {
        console.error('Error:', error);
      }
      console.groupEnd();
    }

    // Send critical errors to external logging service (LogRocket, etc.)
    if (level === 'error' || level === 'critical') {
      this.sendToExternalLogger(entry);
    }
  }

  getLogStyle(level) {
    const styles = {
      debug: 'color: #888; background: #f5f5f5;',
      info: 'color: #0066cc; background: #e6f3ff;',
      warn: 'color: #cc6600; background: #fff3e6;',
      error: 'color: #cc0000; background: #ffe6e6;',
      critical: 'color: white; background: #cc0000; font-weight: bold;'
    };
    return styles[level] || styles.info;
  }

  sendToExternalLogger(entry) {
    try {
      // Send to LogRocket if available
      if (window.LogRocket) {
        window.LogRocket.captureException(new Error(entry.message), {
          tags: { level: entry.level },
          extra: entry.context
        });
      }
    } catch (e) {
      console.error('Failed to send to external logger:', e);
    }
  }

  // Convenience methods
  debug(message, context = {}) {
    this.log('debug', message, context);
  }

  info(message, context = {}) {
    this.log('info', message, context);
  }

  warn(message, context = {}, error = null) {
    this.log('warn', message, context, error);
  }

  error(message, context = {}, error = null) {
    this.log('error', message, context, error);
  }

  critical(message, context = {}, error = null) {
    this.log('critical', message, context, error);
  }

  // API call logging
  logApiCall(method, url, status, response, requestData = null) {
    const context = {
      method,
      url,
      status,
      requestData,
      responseData: response
    };

    if (status >= 200 && status < 300) {
      this.debug(`API ${method} ${url} succeeded`, context);
    } else if (status >= 400 && status < 500) {
      this.warn(`API ${method} ${url} client error`, context);
    } else if (status >= 500) {
      this.error(`API ${method} ${url} server error`, context);
    }
  }

  // Get recent logs for debugging
  getRecentLogs(count = 20) {
    return this.logHistory.slice(-count);
  }

  // Clear log history
  clearHistory() {
    this.logHistory = [];
  }
}

const logger = new Logger();
export default logger;
